import Hls from 'hls.js';
import { rewriteAudioUrl } from './audioUrl';

export type CurrentQuality = 'lossless' | 'aac-256' | null;

export interface AttachStream {
  audioUrl: string;
  hlsUrl?: string;
}

export interface AttachCallbacks {
  /** Fires on ABR level switch with the resolved quality tier. */
  onLevel?: (q: CurrentQuality) => void;
  /** Fires after an unrecoverable HLS error forced the progressive fallback. */
  onFatalFallback?: () => void;
}

/** Destroy any live hls.js instance and clear the ref (leak guard). */
export function detachHls(hlsRef: { current: Hls | null }): void {
  if (hlsRef.current) {
    hlsRef.current.destroy();
    hlsRef.current = null;
  }
}

/**
 * Attach a stream to the shared <audio> element. Destroy-first, then:
 *  - no hlsUrl → legacy progressive audio.src
 *  - native HLS (Safari/iOS) → audio.src = manifest
 *  - Hls.isSupported() → new Hls + loadSource + attachMedia (ABR auto)
 *  - otherwise → progressive fallback
 */
export function attachSource(
  audio: HTMLAudioElement,
  hlsRef: { current: Hls | null },
  stream: AttachStream,
  cbs: AttachCallbacks = {},
): void {
  detachHls(hlsRef);
  // I2 (TEM-1058): reset the quality badge on every attach so a prior stream's
  // tier can't linger. The hls.js path re-establishes it on LEVEL_SWITCHED;
  // native/legacy/progressive paths have no known tier.
  cbs.onLevel?.(null);

  if (!stream.hlsUrl) {
    audio.src = rewriteAudioUrl(stream.audioUrl);
    audio.load();
    return;
  }

  const manifestUrl = rewriteAudioUrl(stream.hlsUrl);

  if (audio.canPlayType('application/vnd.apple.mpegurl')) {
    audio.src = manifestUrl;
    audio.load();
    return;
  }

  if (Hls.isSupported()) {
    const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
    hlsRef.current = hls;
    hls.loadSource(manifestUrl);
    hls.attachMedia(audio);

    hls.on(Hls.Events.LEVEL_SWITCHED, (_evt: unknown, data: { level: number }) => {
      const codec = (hls.levels[data.level]?.audioCodec ?? '').toLowerCase();
      cbs.onLevel?.(codec.includes('flac') ? 'lossless' : 'aac-256');
    });

    hls.on(Hls.Events.ERROR, (_evt: unknown, data: { fatal: boolean; type: string }) => {
      if (!data.fatal) return; // hls.js self-heals non-fatal errors / ABR blacklists a bad level
      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        hls.startLoad();
        return;
      }
      if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
        hls.recoverMediaError();
        return;
      }
      detachHls(hlsRef);
      cbs.onLevel?.(null); // dropped to progressive — clear the tier badge (I2)
      if (stream.audioUrl) {
        audio.src = rewriteAudioUrl(stream.audioUrl);
        audio.load();
      }
      cbs.onFatalFallback?.();
    });
    return;
  }

  audio.src = rewriteAudioUrl(stream.audioUrl);
  audio.load();
}
